
import { defineComponent, inject, PropType, ref, computed, nextTick, watch } from 'vue';
import OverlayModal from './OverlayModal.vue';
import Multiselect from '@/components/Inputs/DropdownInput.vue';
import swal from 'sweetalert2';
import { api } from '@/services/Api';
import TimeInput from '@/components/Inputs/TimeInput.vue';
import RadioInput from '@/components/Inputs/RadioInput.vue';
import Calendar from 'primevue/calendar';
import { useI18n } from 'vue-i18n';
import useSlots from '@/modules/useSlots';
import { ProductListItem } from '@/models/Product';
import { CalendarSlot, CalendarEvent } from '@/models/Calendar';
import { DayOfWeekKendo, OrderBlockOption, OrderBlockTypes } from '@/models/Enums';
import moment from 'moment';
import { Dictionary } from 'lodash';
import InputNumber from 'primevue/inputnumber';
import { store } from '@/store';
import { useRoute } from 'vue-router';

export const CalendarModeList = ['slot', 'event'] as const;
export type CalendarMode = typeof CalendarModeList[number];

export default defineComponent({
    components: { Multiselect, TimeInput, Calendar, RadioInput, OverlayModal, InputNumber },
    name: 'CalendarSlotModal',
    props: {
        products: {
            type: Array as PropType<ProductListItem[]>,
            default: () => []
        },
        showProduct: {
            type: Boolean,
            default: true
        }
    },
    emits: ['save', 'saveEvent', 'close'],
    data() {
        return {};
    },
    async setup(props, { emit }: any) {
        const { t } = useI18n();
        const weekDayNames = DayOfWeekKendo;
        const slotLimit = ref<string | null>(null);
        const endLimit = ref('date');
        const showExtraInfoAbout = ref(false);
        const mode = ref<CalendarMode>('slot');
        const { saveCalendarSlot } = useSlots();
        const selectedOrderBlockType = ref<OrderBlockTypes>(OrderBlockTypes.minutes);
        const selectedOrderBlockOption = ref<OrderBlockOption>(OrderBlockOption.after);
        const orderBlockTypes = orderBlockTypesOpt();
        const orderBlockOptions = orderBlockOptionsOpt();

        const slot = ref<CalendarSlot>(new CalendarSlot());
        const event = ref<CalendarEvent>(new CalendarEvent());
        const isOpen = ref(false);
        const parentSlotDate = ref('');
        const isSlot = computed(() => mode.value == 'slot');
        const canChangeMode = computed(() => (isSlot.value ? !slot.value.id && !slot.value.originalSlotId : !event.value.id));
        const changeDateMode = ref(false);
        const route = useRoute();
        const canCreateChangeSlotInPast = computed(() => store.getters.canCreateChangeSlotInPast as boolean);

        const dayOfWeek = computed(() => {
            const day = moment(slot.value.startDate, moment.ISO_8601).day();
            return Object.values(DayOfWeekKendo)[day] ?? '';
        });


        function orderBlockTypesOpt() {
            return [
                { id: OrderBlockTypes.minutes, name: t('calendar.v3.modal.slot.minutes'), key: 'minutes' },
                { id: OrderBlockTypes.hours, name: t('calendar.v3.modal.slot.hours'), key: 'hours' },
                { id: OrderBlockTypes.days, name: t('calendar.v3.modal.slot.days'), key: 'days' }
            ];
        }

        function orderBlockOptionsOpt() {
            return [
                { id: OrderBlockOption.before, name: t('calendar.v3.modal.slot.before') },
                { id: OrderBlockOption.after, name: t('calendar.v3.modal.slot.after') },
                { id: OrderBlockOption.allow, name: t('calendar.v3.modal.slot.allow') }
            ];
        }
        function reCalculateOrderBlockMin() {
            if (slot.value.orderBlockMinutes == null) {
                slot.value.blockCreatingNewOrdersBefore = false;
                selectedOrderBlockOption.value = OrderBlockOption.allow;
                return;
            }

            if (slot.value.orderBlockMinutes == 0) {
                selectedOrderBlockOption.value = OrderBlockOption.after;
                slot.value.blockCreatingNewOrdersBefore = false;
                return;
            }

            slot.value.blockCreatingNewOrdersBefore = true;
            selectedOrderBlockOption.value = OrderBlockOption.before;
            if (slot.value.orderBlockMinutes % (24 * 60) == 0) {
                selectedOrderBlockType.value = OrderBlockTypes.days;
                slot.value.orderBlockMinutes = slot.value.orderBlockMinutes / 60 / 24;
            } else if (slot.value.orderBlockMinutes % 60 == 0) {
                selectedOrderBlockType.value = OrderBlockTypes.hours;
                slot.value.orderBlockMinutes = slot.value.orderBlockMinutes / 60;
            } else {
                selectedOrderBlockType.value = OrderBlockTypes.minutes;
            }
        }
        watch(
            () => slot.value.productId,
            x => {
                if (props.products && props.products.length > 0) {
                    slot.value.extraInfo = props.products.find(x => x.id === slot.value.productId)?.extraInfo as string;
                }
            }
        );
        const isDate = (val: string | Date) => ((val as any)?.getDate ? true : false);
        function getMinutes(time: string) {
            const [hours, mins] = time.split(':');
            return parseInt(hours) * 60 + parseInt(mins);
        }

        const isProductEditable = ref(true);
        function validate() {
            const hasRepeat = slot.value.repeatMode > 1;
            let limit = false;
            if (slotLimit.value && slotLimit.value == 'count') {
                const l = slot.value.limit ?? -1;
                if (String(l) !== '0') {
                    limit = !((slot.value.limit || 0) * 1) || l < 0 || l > 1_000_000 || l % 1 !== 0;
                }
            }
            return {
                productId: !slot.value.productId,
                startDate: !isDate(slot.value.startDate),
                repeatEndDate:
                    hasRepeat &&
                    endLimit.value == 'date' &&
                    (!isDate(slot.value.repeatEndDate || '') ||
                        (isDate(slot.value.startDate) && (slot.value.startDate as Date) > (slot.value.repeatEndDate as Date))),
                repeatCount: hasRepeat && endLimit.value == 'count' && !((slot.value.repeatCount || 0) * 1),
                limit: limit,
                startTime: !slot.value.startTime,
                endTime: slot.value.startTime && !slot.value.endTime,
                blockCreatingOrdersBefore: slot.value.blockCreatingNewOrdersBefore && !slot.value.orderBlockMinutes
            };
        }
        const invalid = ref(validate());

        function updateEndTime(field: string, val?: string) {
            if (val) {
                if (field == 'start') {
                    slot.value.startTime = val;
                } else if (field == 'end') {
                    slot.value.endTime = val;
                } else if (field == 'duration') {
                    slot.value.duration = val;
                }
            }
            if (!slot.value.startTime) return;
            const startMins = getMinutes(slot.value.startTime);
            const start = moment.utc(0).add(startMins, 'minutes');
            if (field == 'start' || field == 'end') {
                if (!slot.value.endTime) return;

                let mins = getMinutes(slot.value.endTime);
                if (mins < startMins) mins += 24 * 60;
                const end = moment.utc(0).add(mins, 'minutes');

                slot.value.duration = moment.utc(end.diff(start)).format('HH:mm');
                if (slot.value.duration === '00:00') slot.value.duration = '24:00';
            } else if (field == 'duration') {
                if (!slot.value.duration) return;
                const end = start.clone().add(getMinutes(slot.value.duration), 'minutes');
                slot.value.endTime = end.format('HH:mm');
            }
        }

        function normalizeTimeString(time: string): string {
            const hasHoursMinutesSeconds = /^\d{2}:\d{2}:\d{2}$/;
            const hasHoursMinutes = /^\d{2}:\d{2}$/;

            if (hasHoursMinutesSeconds.test(time)) {
                return time; // Already in HH:mm:ss format
            }

            if (hasHoursMinutes.test(time)) {
                return `${time}:00`; // Convert HH:mm to HH:mm:ss
            }

            // Return as-is if format is unrecognized
            return time;
        }

        const back = () => {
            emit('close');
            selectedOrderBlockType.value = OrderBlockTypes.minutes;
            isOpen.value = false;
        };
        const validateSaveSlot = async () => {
            invalid.value = validate();
            // has validation errors;
            if (Object.values(invalid.value).filter(x => x).length > 0) {
                return;
            }
            const model = CalendarSlot.clone(slot.value);
            if (selectedOrderBlockOption.value === OrderBlockOption.after) {
                model.orderBlockMinutes = 0;
            }
            if (selectedOrderBlockOption.value === OrderBlockOption.allow) {
                model.orderBlockMinutes = null;
            }

            if (selectedOrderBlockOption.value === OrderBlockOption.before) {
                if (!model.orderBlockMinutes) {
                    model.orderBlockMinutes = null;
                    return;
                }
                if (model.orderBlockMinutes != null && selectedOrderBlockType.value == OrderBlockTypes.days) {
                    model.orderBlockMinutes = model.orderBlockMinutes * 60 * 24;
                } else if (model.orderBlockMinutes != null && selectedOrderBlockType.value == OrderBlockTypes.hours) {
                    model.orderBlockMinutes = model.orderBlockMinutes * 60;
                }
            }
            model.startDate = moment(model.startDate)
                .format()
                .substr(0, 10);
            // has Repeat
            if (model.repeatMode > 1) {
                if (endLimit.value == 'count') {
                    model.repeatEndDate = null;
                    model.repeatCount = parseInt((model.repeatCount ?? 0).toString());
                } else {
                    model.repeatCount = null;
                    model.repeatEndDate = model.repeatEndDate
                        ? moment(model.repeatEndDate)
                            .format()
                            .substr(0, 10)
                        : null;
                }
            } else {
                model.repeatCount = null;
                model.repeatEndDate = null;
            }
            model.startTime = normalizeTimeString(model.startTime);
            model.endTime = normalizeTimeString(model.endTime);
            model.duration = normalizeTimeString(model.duration);
            if (!slotLimit.value) {
                model.limit = null;
            } else if (slotLimit.value == 'order-qty') {
                emit('add-virtual-slot', model);
                return;
            } else {
                model.limit = (model.limit || 0) * 1;
            }
            if (model.limit === 0) {
                const slotZeroQtyAccepted = await swal.fire({
                    text: t('product.priceList.zeroLimit'),
                    icon: 'question',
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('button.yes'),
                    cancelButtonText: t('button.no')
                });
                if (!slotZeroQtyAccepted.isConfirmed) {
                    return;
                }
            }
            swal.showLoading();
            const savedSlot = await saveCalendarSlot(model, parentSlotDate.value);
            if (!savedSlot) return;

            //Added in preload slots
            store.commit('addPreloadSlot', savedSlot);

            swal.close();
            model.id = (savedSlot as any).slot.id;
            store.state.createdVirtualSlotIds.push(model.id);
            // if virtual slot is not attached - delete it
            const prevSlotId = Number(route.params.slotId);
            if (prevSlotId && !store.state.order.products.find(x => x.slotId == prevSlotId)) {
                await api.deleteCalendarSlot(prevSlotId);
            }
            emit('save', model);
            isOpen.value = false;
        };

        function save() {
            validateSaveSlot();
        }
        function open(newSlot: CalendarSlot, productIsEditable = true, changeDate = false) {
            mode.value = 'slot';
            Object.keys(invalid.value).forEach(k => ((invalid.value as Dictionary<boolean>)[k] = false));
            slot.value = CalendarSlot.clone(newSlot);
            if (!slot.value.duration) {
                updateEndTime('end');
            }
            reCalculateOrderBlockMin();
            parentSlotDate.value = '';
            if (newSlot.originalSlotId) {
                parentSlotDate.value = moment(newSlot.startDate).format('YYYY-MM-DD');
            }
            slotLimit.value = slot.value.limit !== null ? 'count' : 'order-qty';
            endLimit.value = slot.value.repeatCount || 0 > 0 ? 'count' : 'date';
            event.value = new CalendarEvent();
            event.value.startDate = slot.value.startDate;
            event.value.endDate = slot.value.startDate;
            isProductEditable.value = productIsEditable;
            changeDateMode.value = changeDate;
            isOpen.value = true;
        }
        function modeChanged(mode: CalendarMode) {
            slot.value.startDate = event.value.startDate;
            slot.value.startTime = event.value.startTime;
            slot.value.endTime = event.value.endTime;
            updateEndTime('end');
        }

        function setRepeat(mode: number) {
            if (mode == 3) {
                slot.value.daysOfWeek = [];
            } else if (mode == 2) {
                slot.value.daysOfWeek = Object.values(weekDayNames);
            } else {
                mode = 1;
            }
            slot.value.repeatMode = mode;
        }
        function filterSlotLimitInput(event: any) {
            if (event.key.trim() === '' || isNaN(event.key)) {
                event.preventDefault();
            }
        }
        function checkEventDescription() {
            if (event.value.description.length > 300) {
                event.value.description = event.value.description.substring(0, 300);
            }
        }

        function checkExtraInfo() {
            if (slot.value.extraInfo.length > 255) {
                slot.value.extraInfo = slot.value.extraInfo.substring(0, 255);
            }
        }
        function watchAtOption(event) {
            console.log('watchAtOption', event);

            if (event.value === OrderBlockOption.before) {
                slot.value.blockCreatingNewOrdersBefore = true;
            } else {
                slot.value.blockCreatingNewOrdersBefore = false;
            }
            slot.value.orderBlockMinutes = null;
        }
        function setProductOrderBlockMinutes(event) {
            console.log('setProductOrderBlockMinutes');

            slot.value.orderBlockMinutes = props.products.find(x => x.id.toString() === event.value)?.orderBlockMinutes as number;
            reCalculateOrderBlockMin();
        }
        return {
            mode,
            modeChanged,
            back,
            slot,
            isDate,
            weekDayNames,
            isOpen,
            open,
            dayOfWeek,
            slotLimit,
            endLimit,
            getMinutes,
            invalid,
            setRepeat,
            updateEndTime,
            isSlot,
            event,
            canChangeMode,
            save,
            filterSlotLimitInput,
            isProductEditable,
            selectedOrderBlockType,
            orderBlockTypes,
            checkEventDescription,
            checkExtraInfo,
            showExtraInfoAbout,
            watchAtOption,
            selectedOrderBlockOption,
            orderBlockOptions,
            setProductOrderBlockMinutes,
            changeDateMode,
            canCreateChangeSlotInPast
        };
    }
});
